import { ReactNode, useCallback, useMemo, useEffect } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { motion } from 'framer-motion'
import { cn } from '$components'
import { ModalContextProvider } from './context'
import { transition } from './animation'
import { ModalHeader } from './header'
import { ModalContent } from './content'
import { ModalHeading } from './heading'
import { ModalFooter } from './footer'
import { useModalRegistry } from '../../contexts/modal-registry'
import { useKeybind } from '$components'

export type NavigableModalProps = {
  children?: ReactNode,
  className?: string,
  onClose?: () => void,
}

export function NavigableModal({ children, className, onClose }: NavigableModalProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const type = useNavigationType()
  const { openModal, closeModal } = useModalRegistry()

  useEffect(() => {
    openModal()
    return () => closeModal()
  }, [openModal, closeModal])


  const calculatePath = useCallback((segments: string[]) => {
    const segment = segments.pop()

    if (!segment) {
      return '/'
    }

    if (segment.startsWith(':')) {
      calculatePath(segments)
    }

    return segments.join('/')
  }, [])

  const nextRouteDown = useMemo(() => calculatePath(location.pathname.split('/')), [calculatePath, location])

  const handleClose = () => {
    if (onClose) {
      return onClose()
    }

    navigate(nextRouteDown)
  }

  useKeybind({ key: 'Escape', onKeyPress: handleClose })

  return (
    <ModalContextProvider onClose={handleClose}>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed top-0 left-0 bg-black/10 w-full h-full cursor-default z-40"
        onClick={handleClose}
      />

      {/* Modal */}
      <motion.div
        initial={type === 'PUSH' ? 'initial' : 'off'}
        animate="in"
        exit="out"
        variants={transition}
        className={cn(
          'fixed top-0 left-0 w-full h-full flex justify-center z-50 pointer-events-none',
          'items-end [@media(min-width:640px)]:items-center',
        )}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'bg-white flex flex-col max-h-[90%] max-w-[640px] w-full pointer-events-auto',
            '[@media(min-width:640px)]:rounded-b-3xl rounded-t-3xl',
            className,
          )}
        >
          {children}
        </div>
      </motion.div>
    </ModalContextProvider>
  )
}

NavigableModal.Header = ModalHeader
NavigableModal.Content = ModalContent
NavigableModal.Heading = ModalHeading
NavigableModal.Footer = ModalFooter
